import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutDashboard, Building2, Users, Star, Flag, Settings, 
  Check, X, Eye, BadgeCheck, Award, Trash2, ChevronRight, Search,
  HelpCircle, MessageSquare, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { adminAPI, statsAPI } from '../lib/api';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user } = useAuth();
  
  const [stats, setStats] = useState({ facilities: 0, specialists: 0, pharmacies: 0, reviews: 0 });
  const [pendingListings, setPendingListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [claims, setClaims] = useState([]);
  const [flaggedReviews, setFlaggedReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionFilter, setQuestionFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [questionFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, allRes, claimsRes, reviewsRes, usersRes, questionsRes] = await Promise.all([
        statsAPI.get(),
        adminAPI.getListings({ status: 'pending' }),
        adminAPI.getListings({ limit: 100 }),
        adminAPI.getClaims('pending'),
        adminAPI.getReviews('flagged'),
        adminAPI.getUsers({ limit: 50 }),
        adminAPI.getQuestions('all'),
      ]);
      
      setStats(statsRes.data);
      setPendingListings(pendingRes.data.listings || []);
      setAllListings(allRes.data.listings || []);
      setClaims(claimsRes.data || []);
      setFlaggedReviews(reviewsRes.data || []);
      setUsers(usersRes.data.users || []);
      setQuestions(questionsRes.data || []);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const res = await adminAPI.getQuestions(questionFilter);
      setQuestions(res.data || []);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const handleApproveListing = async (listingId) => {
    try {
      await adminAPI.approveListing(listingId);
      toast.success('Listing approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve listing');
    }
  };

  const handleRejectListing = async (listingId) => {
    try {
      await adminAPI.rejectListing(listingId);
      toast.success('Listing rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject listing');
    }
  };

  const handleFeatureListing = async (listingId, featured) => {
    try {
      await adminAPI.featureListing(listingId, featured);
      toast.success(featured ? 'Listing featured' : 'Listing unfeatured');
      loadData();
    } catch (error) {
      toast.error('Failed to update listing');
    }
  };

  const handleApproveClaim = async (claimId) => {
    try {
      await adminAPI.approveClaim(claimId);
      toast.success('Claim approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve claim');
    }
  };

  const handleRejectClaim = async (claimId) => {
    try {
      await adminAPI.rejectClaim(claimId);
      toast.success('Claim rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject claim');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await adminAPI.deleteQuestion(questionId);
      toast.success('Question deleted');
      loadQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    try {
      await adminAPI.updateUserRole(userId, role);
      toast.success('User role updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const pendingQuestions = questions.filter(q => q.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <LayoutDashboard className="w-5 h-5 text-purple-700" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-gray-600 text-sm">Manage listings, users, and reviews</p>
              </div>
            </div>
            <Link to="/admin/seo">
              <Button variant="outline" data-testid="seo-manager-link">
                <Search className="w-4 h-4 mr-2" />
                SEO Manager
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-teal-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.facilities}</p>
                  <p className="text-sm text-gray-600">Facilities</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.specialists}</p>
                  <p className="text-sm text-gray-600">Specialists</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-rose-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-rose-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.pharmacies}</p>
                  <p className="text-sm text-gray-600">Pharmacies</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Star className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.reviews}</p>
                  <p className="text-sm text-gray-600">Reviews</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-purple-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{questions.length}</p>
                  <p className="text-sm text-gray-600">Questions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Alert */}
        {pendingListings.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Badge className="bg-amber-600 text-white">{pendingListings.length}</Badge>
              </div>
              <div>
                <p className="font-medium text-amber-800">Pending Listings</p>
                <p className="text-sm text-amber-700">Review and approve new submissions</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-600" />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList>
            <TabsTrigger value="listings" data-testid="admin-listings-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Listings
              {pendingListings.length > 0 && (
                <Badge className="ml-2 bg-amber-500">{pendingListings.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="claims" data-testid="admin-claims-tab">
              <BadgeCheck className="w-4 h-4 mr-2" />
              Claims
              {claims.length > 0 && (
                <Badge className="ml-2 bg-blue-500">{claims.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-reviews-tab">
              <Flag className="w-4 h-4 mr-2" />
              Flagged Reviews
            </TabsTrigger>
            <TabsTrigger value="questions" data-testid="admin-questions-tab">
              <HelpCircle className="w-4 h-4 mr-2" />
              Q&A
              {pendingQuestions > 0 && (
                <Badge className="ml-2 bg-purple-500">{pendingQuestions}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-users-tab">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <Card>
              <CardHeader>
                <CardTitle>Manage Listings</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allListings.map((listing) => (
                      <TableRow key={listing.listing_id}>
                        <TableCell className="font-medium">{listing.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{listing.listing_type}</Badge>
                        </TableCell>
                        <TableCell>{listing.city}, {listing.state}</TableCell>
                        <TableCell>
                          <Badge className={
                            listing.status === 'approved' ? 'bg-green-100 text-green-700' :
                            listing.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                          }>
                            {listing.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`badge-plan ${listing.plan}`}>{listing.plan}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {listing.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600"
                                  onClick={() => handleApproveListing(listing.listing_id)}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => handleRejectListing(listing.listing_id)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant={listing.featured ? 'default' : 'outline'}
                              className={listing.featured ? 'bg-amber-500 hover:bg-amber-600' : ''}
                              onClick={() => handleFeatureListing(listing.listing_id, !listing.featured)}
                            >
                              <Award className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Claims Tab */}
          <TabsContent value="claims">
            <Card>
              <CardHeader>
                <CardTitle>Pending Claims</CardTitle>
              </CardHeader>
              <CardContent>
                {claims.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Listing</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {claims.map((claim) => (
                        <TableRow key={claim.claim_id}>
                          <TableCell className="font-medium">{claim.listing_id}</TableCell>
                          <TableCell>{claim.user_email || claim.user_id}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {claim.business_role || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell className="capitalize">{claim.verification_method}</TableCell>
                          <TableCell>{new Date(claim.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApproveClaim(claim.claim_id)}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={() => handleRejectClaim(claim.claim_id)}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No pending claims
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle>Flagged Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                {flaggedReviews.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Review</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flaggedReviews.map((review) => (
                        <TableRow key={review.review_id}>
                          <TableCell className="max-w-xs truncate">{review.text}</TableCell>
                          <TableCell>{review.rating}/5</TableCell>
                          <TableCell>{review.user_name}</TableCell>
                          <TableCell>{new Date(review.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No flagged reviews
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Q&A Tab */}
          <TabsContent value="questions">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Manage Questions</CardTitle>
                  <Select value={questionFilter} onValueChange={setQuestionFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Questions</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="answered">Answered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {questions.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Question</TableHead>
                        <TableHead>Listing</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {questions.map((question) => (
                        <TableRow key={question.question_id}>
                          <TableCell className="max-w-xs">
                            <p className="truncate font-medium">{question.text}</p>
                            {question.answer && (
                              <p className="text-xs text-gray-500 truncate mt-1">
                                <MessageSquare className="w-3 h-3 inline mr-1" />
                                {question.answer}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <Link 
                              to={`/listing/${question.listing_id}`}
                              className="text-teal-600 hover:underline flex items-center gap-1"
                            >
                              {question.listing_name}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          </TableCell>
                          <TableCell>{question.user_name}</TableCell>
                          <TableCell>
                            <Badge className={
                              question.status === 'answered' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-amber-100 text-amber-700'
                            }>
                              {question.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(question.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => handleDeleteQuestion(question.question_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No questions found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Manage Users</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Badge className={
                            u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                            u.role === 'owner' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }>
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Select
                            value={u.role}
                            onValueChange={(role) => handleUpdateUserRole(u.user_id, role)}
                          >
                            <SelectTrigger className="w-[120px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="owner">Owner</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
